import { FC, useEffect, useState } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { ChatModal } from "src/components/bookings";
import arrowLeft from "src/assets/images/common/arrowLeft.svg";
import close from "src/assets/images/common/close.svg";
import white_mark from "src/assets/images/common/white_mark.svg";
import { theme } from "src/styles/Theme";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Flex,
  Button,
  ButtonClass,
  ConfirmationModal,
} from "src/components/ui";
import avatar from "src/assets/images/header/avatar.svg";
import { useLoading } from "src/hooks";
import { ScaleLoader } from "react-spinners";
import { formatDateDmy, formatTime } from "src/utils";
import { toast, ToastContainer } from "react-toastify";
import { Check } from "@mui/icons-material";
import CallLogsModal from "src/components/bookings/CallLogsModal";
import BookingInvoicesModal from "src/components/bookings/BookingInvoicesModal";
import BookingTimelineStepper from "./BookingTimelineStepper";
import {
  useBookingDetailsQuery,
  useGetMessagesQuery,
  useCancelBookingMutation,
  useCompleteBookingMutation,
  useAcceptBookingMutation,
} from "src/store/services/bookingApi";
import {
  useLazyGetBookingTimelineQuery,
  useGetBookingTimelineQuery,
} from "src/store/services/partnerApi";
import { auth } from "src/store/Auth";

export const RhsHeading: FC<{ tabStatus: string | null }> = ({ tabStatus }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBackToBookings = () => {
    // Get the original tab from navigation state, fallback to 'all' if not available
    const fromTab = (location.state as any)?.fromTab || "all";
    navigate(`/bookings?tabStatus=${fromTab}`);
  };

  const centralHandleBackToBookings = () => {
    // Get the original tab from navigation state, fallback to 'all' if not available
    const fromTab = (location.state as any)?.fromTab || "all";
    navigate(`/partners/group/${authUser._id}`);
  };

  const [showImportant, setShowImportant] = useState(true);

  const { authUser } = auth.use();

  useEffect(() => {
    if (authUser?.role?.includes("centralPartnerManager")) {
      setShowImportant(false);
    }
  }, [authUser]);
  if (showImportant) {
    return (
      <Flex wrap="wrap">
        <Button
          classes={`${[ButtonClass.SOLID, ButtonClass.WITH_ICON]}`}
          style={{ backgroundColor: theme.colors.purple }}
          onClick={handleBackToBookings}
        >
          <img src={arrowLeft} alt="back" />
          <span>Back to Bookings</span>
        </Button>
      </Flex>
    );
  } else {
    return (
      <Flex wrap="wrap">
        <Button
          classes={`${[ButtonClass.SOLID, ButtonClass.WITH_ICON]}`}
          style={{ backgroundColor: theme.colors.purple }}
          onClick={centralHandleBackToBookings}
        >
          <img src={arrowLeft} alt="back" />
          <span>Back to Outlets</span>
        </Button>
      </Flex>
    );
  }
};

const initialAddressState = {
  city: "",
  state: "",
  house_address: "",
  street: "",
};

const initialMetaState = {
  call_out_fee: 0,
  display_picture: "",
  first_name: "",
  last_name: "",
  phone: "",
  rating: "",
  address: initialAddressState,
  tier: "",
  business_name: "",
  shop_address: "",
};

const CategoryTypes = {
  _id: "",
  name: "",
  description: "",
  image_url: "",
};

export const initialBookingState = {
  artisan_id: "",
  city: "",
  service: "",
  state: "",
  booking_type: "",
  status: "",
  createdAt: "",
  user_id: "",
  description: "",
  fees: {
    commitment_fee: {
      amount: 0,
      status: "",
    },
    service_fee: {
      amount: 0,
      status: "",
    },
    facilitation_fee: {
      amount: 0,
      status: "",
    },
  },
  artisan_meta: initialMetaState,
  user_meta: initialMetaState,
  category: CategoryTypes,
};

const initialMessengersState = {
  artisan_meta: {
    first_name: "",
    last_name: "",
    display_picture: "",
    phone: "",
    business_name: "",
  },
  user_meta: {
    first_name: "",
    last_name: "",
    display_picture: "",
    phone: "",
  },
};

const ProfileCard = ({
  src,
  name,
  rating,
  tier,
  badgeText,
  badgeColor,
  loading,
  isPartnerBooking,
}: {
  src: string;
  name: string;
  rating: number | string;
  tier: string;
  badgeText: string;
  badgeColor: string;
  loading: boolean;
  isPartnerBooking: boolean;
}) => (
  <div
    className="relative w-[340px] h-[340px] rounded-3xl overflow-hidden flex flex-col justify-center bg-center bg-cover"
    style={{ backgroundImage: `url(${src})` }}
  >
    {/* Overlay */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/10  to-black/60 z-20" />
    {/* Badge */}
    <span
      className={`absolute top-5 left-5 px-4 py-1 rounded-xl font-bold text-lg z-20 shadow ${
        badgeText === "Customer" ? "text-purple-600" : "text-yellow-500"
      } bg-white`}
    >
      {badgeText}
    </span>
    {/* Name */}
    <div className="absolute inset-0 left-0 right-0 bottom-1 bg-black/10 flex flex-col justify-end items-center z-20 pb-4 space-y-2">
      <div className="text-white font-bold text-2xl drop-shadow text-center">
        {name}
      </div>
      {/* Rating & Tier */}
      <div className="flex gap-3 items-center justify-center">
        {/* Rating pill */}
        <span className="flex items-center bg-white rounded-full px-4 py-1 font-semibold text-lg text-gray-900 shadow">
          <img src={`/svgs/star.svg`} alt="star" className="w-5 mr-1.5" />
          {rating}
        </span>
        {/* Tier pill */}
        {!isPartnerBooking && (
          <span className="flex items-center bg-white rounded-full px-4 py-1 font-semibold text-lg text-gray-900 shadow">
            {tier && tier !== "N/A" ? (
              <>
                Tier {tier}
                <img
                  src={`/svgs/tier${tier}.svg`}
                  alt={`Tier ${tier}`}
                  className="w-5 ml-1.5"
                />
              </>
            ) : (
              "Tier: N/A"
            )}
          </span>
        )}
      </div>
    </div>
    {loading && (
      <div className="absolute top-1/2 left-1/2 z-30 -translate-x-1/2 -translate-y-1/2">
        <ScaleLoader color={theme.colors.purple} height={50} width={8} />
      </div>
    )}
  </div>
);

const StatCard = ({
  icon,
  title,
  count,
  onClick,
}: {
  icon: string;
  title: string;
  count: number | string;
  onClick?: () => void;
}) => (
  <div
    className={`bg-white rounded-xl border border-gray-200 px-8 py-6 min-w-[300px] flex items-center relative gap-5 ${
      onClick ? "hover:shadow-md transition" : ""
    }`}
  >
    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mr-4">
      <img src={icon} alt={title} className="w-7 h-7" />
    </div>
    <div className="flex-1">
      <div className="font-semibold text-xl text-gray-900 mb-0.5">{title}</div>
      <div className="font-bold text-2xl text-gray-900">{count}</div>
    </div>

    <img
      src={"/svgs/expand.svg"}
      alt="expand"
      onClick={onClick}
      className="absolute top-4 right-4 w-5 h-5 opacity-80 cursor-pointer"
    />
  </div>
);

const Badge = ({ text, color }: { text: string; color: string }) => (
  <span
    style={{
      position: "absolute",
      top: 16,
      left: 16,
      background: color,
      color: "#fff",
      borderRadius: 12,
      padding: "2px 12px",
      fontWeight: 600,
      fontSize: 14,
      zIndex: 2,
    }}
  >
    {text}
  </span>
);

// StarRating component for 0-5 stars
const StarRating = ({ value }: { value: number }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) =>
      i <= value ? (
        <svg
          key={i}
          className="w-6 h-6 text-orange-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" />
        </svg>
      ) : (
        <svg
          key={i}
          className="w-6 h-6 text-orange-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z"
          />
        </svg>
      ),
    )}
  </div>
);

const BookingDetailsPage = () => {
  const navigate = useNavigate();
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const handleOpenInvoiceModal = () => setOpenInvoiceModal(true);
  const handleCloseInvoiceModal = () => setOpenInvoiceModal(false);

  const [openChatModal, setOpenChatModal] = useState(false);
  const [openConfirmationModal, setOpenCancelConfirmationModal] =
    useState(false);
  const [openComplete, setOpenComplete] = useState(false);
  const handleOpenChatModal = () => {
    setOpenChatModal(true);
  };

  const [methodM, setmethodM] = useState("Pickup");
  const [pDTime, setPDTime] = useState("");
  const [dTime, setDTime] = useState("");
  const handleCloseChatModal = () => setOpenChatModal(false);
  const handleOpenConfirmationModal = () =>
    setOpenCancelConfirmationModal(() => !openConfirmationModal);
  const handleOpenComplete = () => setOpenComplete(() => !openComplete);
  const [bookingsDetail, setBookingDetail] =
    useState<BookingsTypes>(initialBookingState);
  const [bookingTransactions, setBookingTransactions] = useState<
    TransactionTypes[]
  >([]);
  const [escrowTransactions, setEscrowTransactions] = useState<
    TransactionTypes[]
  >([]);
  const [callLogs, setCallLogs] = useState<CallLogs[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const { loading, startLoading, stopLoading } = useLoading(false);
  const {
    loading: confirmationLoading,
    startLoading: startConfirmationLoading,
    stopLoading: stopConfirmationLoading,
  } = useLoading(false);
  const [isAcceptModalShown, setIsAcceptModalShownn] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const { id } = useParams();
  const [openCallLogsModal, setOpenCallLogsModal] = useState(false);
  // const [booking]
  const [openBookingInvoicesModal, setOpenBookingInvoicesModal] =
    useState(false);
  const [messengers, setMessengers] = useState(initialMessengersState);

  const { data: bookingData, isLoading: isBookingLoading } =
    useBookingDetailsQuery(id as string, {
      skip: !id,
    });

  const [
    getBookingTimeline,
    { data: booking_timeline, isLoading: is_booking_time_loading },
  ] = useLazyGetBookingTimelineQuery();

  useEffect(() => {
    if (id) {
      console.log("Calling timeline endpoint...");
      getBookingTimeline({ id });
      console.log(booking_timeline);
    }
  }, [id]);

  const timelineEvents = booking_timeline?.payload?.timeline || [];

  const { data: messagesData, isLoading: isMessagesLoading } =
    useGetMessagesQuery(id as string, {
      skip: !id,
    });
  const [cancelBooking, { isLoading: isCancelling }] =
    useCancelBookingMutation();
  const [completeBooking, { isLoading: isCompleting }] =
    useCompleteBookingMutation();
  const [acceptBooking, { isLoading: isAccepting }] =
    useAcceptBookingMutation();

  const [showImportant, setShowImportant] = useState(true);

  const { authUser } = auth.use();

  useEffect(() => {
    if (authUser?.role?.includes("centralPartnerManager")) {
      setShowImportant(false);
    }
  }, [authUser]);

  useEffect(() => {
    const payload: any = bookingData?.payload?.data;
    if (!payload) return;
    setBookingDetail(payload?.booking || initialBookingState);
    setBookingTransactions(payload?.booking_trx || []);
    setEscrowTransactions(payload?.booking_escrow_trx || []);
    setCallLogs(payload?.call_log || []);
  }, [bookingData]);

  useEffect(() => {
    const payload: any = messagesData?.payload?.data;
    if (!payload) return;
    setMessages(payload?.messages || []);
    setMessengers(payload || initialMessengersState);
  }, [messagesData]);

  let [searchParams, setSearchParams] = useSearchParams();

  const handleCancelBooking = async () => {
    startConfirmationLoading();
    try {
      const res: any = await cancelBooking(String(id)).unwrap();
      toast.success(res?.message || "Booking canceled");
      setBookingDetail({
        ...bookingsDetail,
        status: "Canceled",
      });
      handleOpenConfirmationModal();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || "Cancel failed");
    } finally {
      stopConfirmationLoading();
    }
  };

  const handleCompleteBooking = async () => {
    startConfirmationLoading();
    try {
      const res: any = await completeBooking(String(id)).unwrap();
      toast.success(res?.message || "Booking completed");
      setBookingDetail({
        ...bookingsDetail,
        status: "Completed",
      });
      handleOpenComplete();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || "Completion failed");
    } finally {
      stopConfirmationLoading();
    }
  };
  const handleAcceptBooking = async () => {
    setAcceptLoading(true);
    try {
      const res: any = await acceptBooking(String(id)).unwrap();
      toast.success(res?.message || "Booking accepted");
      setBookingDetail({
        ...bookingsDetail,
        status: "Accepted",
      });
      setIsAcceptModalShownn(false);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || "Accept failed");
    } finally {
      setAcceptLoading(false);
    }
  };
  console.log(bookingsDetail);

  useEffect(() => {
    setmethodM(bookingsDetail?.delivery_info?.delivery_type || "");
    setPDTime(
      bookingsDetail?.delivery_info?.pickup_date
        ? formatTime(new Date(bookingsDetail.delivery_info.pickup_date))
        : "",
    );

    if(bookingsDetail?.delivery_info?.delivery_type == 'delivery_internal'){
      setPDTime(bookingsDetail?.user_meta?.address?.street || '')
    }
  }, [bookingsDetail]);

  const status = bookingsDetail?.status.toLowerCase();
  const isPartnerBooking = bookingsDetail?.booking_type === "partner";

  console.log(isPartnerBooking);
  return (
    <DashboardLayout
      pageTitle={`${isPartnerBooking ? "Order Details" : "Booking Details"}`}
      rhsHeading={
        <RhsHeading tabStatus={searchParams.get("tabStatus") || null} />
      }
    >
      <ToastContainer />
      <ConfirmationModal
        open={openConfirmationModal}
        handleClose={handleOpenConfirmationModal}
        message="Are you sure you want to cancel this booking?"
        loading={confirmationLoading}
        approve={handleCancelBooking}
      />
      <ConfirmationModal
        open={openComplete}
        handleClose={handleOpenComplete}
        message="Are you sure you want to complete this booking?"
        loading={confirmationLoading}
        approve={handleCompleteBooking}
      />
      <ConfirmationModal
        open={isAcceptModalShown}
        handleClose={() => setIsAcceptModalShownn(false)}
        message="Are you sure you want to accept this booking?"
        loading={acceptLoading}
        approve={handleAcceptBooking}
      />
      <div className="px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3">
          <div
            onClick={() =>
              showImportant
                ? navigate(`/customers/${bookingsDetail?.user_id}`)
                : ""
            }
            style={{ cursor: "pointer" }}
          >
            <ProfileCard
              loading={isBookingLoading}
              src={bookingsDetail?.user_meta.display_picture || avatar}
              name={`${bookingsDetail?.user_meta.first_name} ${bookingsDetail?.user_meta.last_name}`}
              rating={bookingsDetail?.user_meta.rating}
              tier={bookingsDetail?.user_meta?.tier || "N/A"}
              badgeText="Customer"
              badgeColor={theme.colors.purple}
              isPartnerBooking={false}
            />
          </div>
          <div
            onClick={() =>
              showImportant
                ? navigate(`/vendors/${bookingsDetail?.artisan_id}`)
                : ""
            }
            style={{ cursor: "pointer" }}
          >
            <ProfileCard
              loading={isBookingLoading}
              src={bookingsDetail?.artisan_meta.display_picture || avatar}
              name={
                bookingsDetail?.booking_type === "individual"
                  ? `${messengers?.artisan_meta?.first_name} ${messengers?.artisan_meta?.last_name}`
                  : messengers?.artisan_meta?.business_name
              }
              rating={bookingsDetail?.artisan_meta.rating}
              tier={bookingsDetail?.artisan_meta?.tier || "N/A"}
              badgeText={isPartnerBooking ? "Partner" : "Vendor"}
              badgeColor={theme.colors.mustard}
              isPartnerBooking={isPartnerBooking}
            />
          </div>

          <div className="w-full space-y-2">
            <StatCard
              icon={"/svgs/phone.svg"}
              title="Call Logs"
              count={callLogs?.length || 0}
              onClick={() => setOpenCallLogsModal(true)}
            />
            <StatCard
              icon={"/svgs/invoice.svg"}
              title={`${
                isPartnerBooking ? "Order Invoices" : "Booking Invoices"
              }`}
              count={bookingTransactions?.length || 0}
              onClick={() => setOpenBookingInvoicesModal(true)}
            />
            <StatCard
              icon={"/svgs/chats.svg"}
              title="Chats"
              count={messages?.length || 0}
              onClick={handleOpenChatModal}
            />
          </div>
        </div>
        <BookingTimelineStepper
          timeline={timelineEvents}
          isLoading={is_booking_time_loading}
          type={methodM} // or "pickup"
          isPartner={isPartnerBooking}
          setDTime={setDTime}
        />
        <div className="bg-white rounded-xl shadow p-0 overflow-hidden mb-8 mt-4">
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left md:truncate">
                  Category
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {isPartnerBooking
                    ? bookingsDetail?.category?.name
                    : bookingsDetail.service}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  Description
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {bookingsDetail?.description || "-"}
                </td>
              </tr>
              <tr
                className={`${
                  status === "canceled" || status === "rejected"
                    ? "bg-red-50"
                    : status === "completed"
                      ? "bg-yellow-50"
                      : status === "active"
                        ? "bg-green-50"
                        : "bg-purple-50"
                }`}
              >
                <td
                  className={`font-semibold px-6 py-4 ${
                    status === "canceled" || status === "rejected"
                      ? "text-red-700"
                      : status === "completed"
                        ? "text-yellow-700"
                        : status === "active"
                          ? "text-green-700"
                          : "text-purple-700"
                  }`}
                >
                  {isPartnerBooking ? "Order Status" : "Booking Status"}
                </td>
                <td
                  className={`text-right px-6 py-4 ${
                    status === "canceled" || status === "rejected"
                      ? "text-red-700"
                      : status === "completed"
                        ? "text-yellow-700"
                        : status === "active"
                          ? "text-green-700"
                          : "text-purple-700"
                  }`}
                >
                  {bookingsDetail.status || "-"}
                </td>
              </tr>
              {/* Show reason for cancellation if status is canceled */}
              {(status === "canceled" || status === "rejected") && (
                <>
                  <tr>
                    <td className="font-semibold px-6 py-4 text-left">
                      Reason for{" "}
                      {status === "canceled" ? "Cancellation" : "Rejection"}
                    </td>
                    <td className="text-right px-6 py-4 text-gray-500">
                      {bookingsDetail.reason || "No reason provided"}
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold px-6 py-4 text-left">
                      Cancelled By
                    </td>
                    <td className="text-right px-6 py-4 text-gray-500">
                      {bookingsDetail.cancelled_by || "N/A"}
                    </td>
                  </tr>
                </>
              )}
              {/* Show ratings only if status is completed */}
              {status === "completed" && (
                <>
                  <tr>
                    <td className="font-semibold px-6 py-4 text-left">
                      Customer Post-booking Rating
                    </td>
                    <td className="text-right px-6 py-4 flex items-center justify-end">
                      <StarRating value={0} />
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold px-6 py-4 text-left">
                      {isPartnerBooking
                        ? "Partner Post-order "
                        : "Vendor Post-order "}
                      Rating
                    </td>
                    <td className="text-right px-6 py-4 flex items-center justify-end">
                      <StarRating value={0} />
                    </td>
                  </tr>
                </>
              )}
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  Start Date
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {bookingsDetail.createdAt
                    ? formatDateDmy(bookingsDetail.createdAt)
                    : "-"}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  Start Time
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {bookingsDetail.createdAt
                    ? formatTime(new Date(bookingsDetail.createdAt))
                    : "-"}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  End Date
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {bookingsDetail.endDate
                    ? formatDateDmy(bookingsDetail.endDate)
                    : "-"}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  End Time
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {bookingsDetail.endDate
                    ? formatTime(new Date(bookingsDetail.endDate))
                    : "-"}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  {isPartnerBooking ? "Partner" : "Vendor"} Location
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {isPartnerBooking
                    ? bookingsDetail.artisan_meta.shop_address
                    : bookingsDetail.artisan_meta.address.street || "-"}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 border-b border-gray-200 text-left">
                  Customer Location
                </td>
                <td className="text-right px-6 py-4 border-b border-gray-200">
                  {bookingsDetail.user_meta.user_current_address?.street || "-"}
                </td>
              </tr>
              <tr>
                <td className="font-semibold px-6 py-4 text-left">
                  {isPartnerBooking
                    ? `${methodM === "delivery_internal" ? "Delivery Location" : "Pickup Time"} `
                    : "Service Location"}
                </td>
                <td className="text-right px-6 py-4">
                  {isPartnerBooking
                    ? pDTime
                    : bookingsDetail.user_meta.address.street || "-"}
                  {/* {methodM} */}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* <InvoiceModal
					open={openInvoiceModal}
					handleClose={handleCloseInvoiceModal}
					bookingTransaction={bookingTransactions}
				/> */}
        <ChatModal
          open={openChatModal}
          handleClose={handleCloseChatModal}
          customerImg={bookingsDetail?.user_meta.display_picture || avatar}
          vendorImg={bookingsDetail?.artisan_meta.display_picture || avatar}
          messages={messages}
          setMessages={setMessages}
          messengers={messengers}
          b_type={bookingsDetail?.booking_type}
        />
        <CallLogsModal
          open={openCallLogsModal}
          handleClose={() => setOpenCallLogsModal(false)}
          callLogs={callLogs}
          customerName={`${bookingsDetail?.user_meta.first_name} ${bookingsDetail?.user_meta.last_name}`}
          vendorName={
            bookingsDetail?.booking_type === "individual"
              ? `${bookingsDetail?.artisan_meta?.first_name} ${bookingsDetail?.artisan_meta?.last_name}`
              : bookingsDetail?.artisan_meta?.business_name
          }
          customerAvatar={bookingsDetail?.user_meta.display_picture || avatar}
          vendorAvatar={bookingsDetail?.artisan_meta.display_picture || avatar}
        />
        <BookingInvoicesModal
          open={openBookingInvoicesModal}
          handleClose={() => setOpenBookingInvoicesModal(false)}
          bookingTransaction={bookingTransactions}
          escrowTransactions={escrowTransactions}
          customerName={`${bookingsDetail?.user_meta.first_name} ${bookingsDetail?.user_meta.last_name}`}
          vendorName={
            bookingsDetail?.booking_type === "individual"
              ? `${bookingsDetail?.artisan_meta?.first_name} ${bookingsDetail?.artisan_meta?.last_name}`
              : bookingsDetail?.artisan_meta?.business_name
          }
          customerAvatar={bookingsDetail?.user_meta.display_picture || avatar}
          vendorAvatar={bookingsDetail?.artisan_meta.display_picture || avatar}
          currency_symbol={bookingsDetail?.artisan_meta?.currency_symbol || "₦"}
        />
        {(status === "pending" || status === "active") && (
          <div className="flex gap-4 justify-center items-center mt-8">
            <button
              className={`bg-red-800 text-white rounded-lg flex items-center justify-center gap-2 p-3`}
              onClick={handleOpenConfirmationModal}
              style={{ backgroundColor: theme.colors.red, minWidth: 180 }}
            >
              <img src={close} alt="x" />
              <span>
                {isPartnerBooking ? "Cancel Order" : "Cancel Booking"}
              </span>
            </button>
            {dTime && (
              <button
                className={`bg-green-800 text-white rounded-lg flex items-center justify-center gap-2 p-3`}
                onClick={handleOpenComplete}
                style={{ minWidth: 180 }}
              >
                <img src={white_mark} alt="" />
                <span>
                  {isPartnerBooking ? "Complete Order" : "Complete Booking"}
                </span>
              </button>
            )}
            {status !== "active" && (
              <button
                className={`bg-green-800 text-white rounded-lg flex items-center justify-center gap-2 p-3`}
                style={{
                  backgroundColor: "green",
                  minWidth: 180,
                  whiteSpace: "nowrap",
                }}
                onClick={() => setIsAcceptModalShownn(true)}
              >
                <Check />
                <span>
                  {isPartnerBooking ? "Accept Order" : "Accept Booking"}
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BookingDetailsPage;
